// CharacterController
// Features:
// - Controls player movement (WASD + Crouch)
// - Handles head bobbing and smooth camera follow
// - Manages footstep sounds and collision physics

import { useEffect, useRef } from 'react'
import * as THREE from 'three'
import { useFrame, useThree } from '@react-three/fiber'
import { useKeyboardControls } from '@react-three/drei'
import { RigidBody, BallCollider } from '@react-three/rapier'

import { useStore } from '../store/useStore'

const PLAYER_CONFIG = {
    startPos: new THREE.Vector3(0.536, 2.3, 0.583),
    speed: 3.0,
    radius: 0.3,
    bobAmplitude: 0.16,
    bobSpeedFactor: 1.8,
    cameraOffset: new THREE.Vector3(0, 0, 0),
}

export default function CharacterController() {
    const [subscribeKeys, getKeys] = useKeyboardControls()
    const controlsEnabled = useStore((state) => state.characterControlsEnabled)
    const camera = useThree((state) => state.camera)

    const bodyRef = useRef()
    const footRef = useRef()

    // --- State ---
    // Grouping mutable state refs to keep them organized
    const state = useRef({
        smoothedCamPos: new THREE.Vector3(),
        bobPhase: 0,
        bobOffset: 0,
        squatOffset: 0,
        prevBobSin: 0,
    })

    // --- Temporary Vectors ---
    // Reusable vectors to avoid garbage collection in the frame loop
    const vec = useRef({
        move: new THREE.Vector3(),
        forward: new THREE.Vector3(),
        right: new THREE.Vector3(),
        tmpPos: new THREE.Vector3(),
    })

    // --- Initialization ---
    useEffect(() => {
        camera.position.copy(PLAYER_CONFIG.startPos)

        // Setup Footsteps Audio
        const audio = new Audio('/sounds/footstep.mp3')
        audio.preload = 'auto'
        audio.loop = false
        audio.volume = 0.7
        footRef.current = audio

        return () => {
            if (footRef.current) {
                footRef.current.pause()
                footRef.current = null
            }
        }
    }, [camera])

    // --- Handle Tab Switching ---
    // Prevents camera jumps when returning to the tab by resetting smoothing
    useEffect(() => {
        const onVisibilityChange = () => {
            if (document.visibilityState !== 'visible' || !bodyRef.current)
                return

            const p = bodyRef.current.translation()
            const s = state.current
            const v = vec.current

            // Snap camera to player position immediately
            v.tmpPos.set(p.x, p.y, p.z).add(PLAYER_CONFIG.cameraOffset)
            s.smoothedCamPos.copy(v.tmpPos)
            s.bobOffset = 0
            s.squatOffset = 0
            s.prevBobSin = 0
        }
        document.addEventListener('visibilitychange', onVisibilityChange)
        return () =>
            document.removeEventListener('visibilitychange', onVisibilityChange)
    }, [])

    // --- Main Game Loop ---
    useFrame((_, dt) => {
        if (!bodyRef.current) return

        // Clamp delta time to prevent physics explosions on lag spikes
        const dtClamped = Math.min(dt, 0.05)

        // 1. Input Handling
        const { w, s, a, d, c } = controlsEnabled
            ? getKeys()
            : { w: 0, s: 0, a: 0, d: 0, c: 0 }
        const sRef = state.current
        const vRef = vec.current

        // 2. Calculate Movement Direction relative to Camera
        camera.getWorldDirection(vRef.forward)
        vRef.forward.y = 0
        vRef.forward.normalize()
        vRef.right
            .crossVectors(vRef.forward, new THREE.Vector3(0, 1, 0))
            .negate()

        vRef.move.set(0, 0, 0)
        if (w) vRef.move.add(vRef.forward)
        if (s) vRef.move.add(vRef.forward.clone().multiplyScalar(-1))
        if (a) vRef.move.add(vRef.right)
        if (d) vRef.move.add(vRef.right.clone().multiplyScalar(-1))
        if (vRef.move.lengthSq() > 0) vRef.move.normalize()

        // 3. Apply Physics Velocity
        const speed = controlsEnabled ? PLAYER_CONFIG.speed : 0
        const vel = vRef.move.multiplyScalar(speed)
        const currentLinVel = bodyRef.current.linvel()

        // Preserve vertical velocity (gravity), overwrite horizontal
        bodyRef.current.setLinvel(
            { x: vel.x, y: currentLinVel.y, z: vel.z },
            true
        )

        // 4. Camera Follow Calculation
        const p = bodyRef.current.translation()
        vRef.tmpPos.set(p.x, p.y, p.z).add(PLAYER_CONFIG.cameraOffset)

        // 5. Head Bobbing Logic
        const speedXZ = Math.hypot(vel.x, vel.z)

        if (speedXZ > 0.0001) {
            // Advance bob phase based on speed
            sRef.bobPhase +=
                dtClamped * (speedXZ * PLAYER_CONFIG.bobSpeedFactor)

            // Calculate target bob height
            const targetBob =
                Math.sin(sRef.bobPhase * 2.0) *
                PLAYER_CONFIG.bobAmplitude *
                Math.min(1, speedXZ / PLAYER_CONFIG.speed)

            // Smoothly interpolate current bob offset
            sRef.bobOffset = THREE.MathUtils.lerp(
                sRef.bobOffset,
                targetBob,
                Math.min(1, 10 * dtClamped)
            )

            // Trigger Footstep Sound at the bottom of the bob cycle
            const bobSin = Math.sin(sRef.bobPhase * 2.0)
            const isMoving = w || a || s || d

            if (isMoving && speedXZ > 0.2 && footRef.current) {
                // Check if we just crossed the threshold downwards
                if (sRef.prevBobSin > -0.9 && bobSin <= -0.9) {
                    footRef.current.currentTime = 0
                    footRef.current.play().catch(() => {})
                }
            }
            sRef.prevBobSin = bobSin
        } else {
            // Decay bob when stopped
            sRef.bobOffset = THREE.MathUtils.lerp(
                sRef.bobOffset,
                0,
                Math.min(1, 6 * dtClamped)
            )
            sRef.prevBobSin = 0
        }

        // 6. Crouch Animation
        const squatTarget = c ? -0.8 : 0.0
        sRef.squatOffset = THREE.MathUtils.lerp(
            sRef.squatOffset,
            squatTarget,
            Math.min(1, 12 * dtClamped)
        )

        // Apply vertical offsets to target camera position
        vRef.tmpPos.y += sRef.bobOffset + sRef.squatOffset

        // 7. Smooth Camera Movement
        // If camera smoothing hasn't started, snap immediately
        if (sRef.smoothedCamPos.lengthSq() === 0)
            sRef.smoothedCamPos.copy(vRef.tmpPos)

        // Lerp camera towards target position for smoothness
        sRef.smoothedCamPos.lerp(vRef.tmpPos, Math.min(1, 5 * dtClamped))
        camera.position.copy(sRef.smoothedCamPos)
    })

    return (
        <group>
            <RigidBody
                ref={bodyRef}
                type="dynamic"
                canSleep={false}
                enabledRotations={[false, false, false]} // Lock rotation physics
                enabledTranslations={[true, false, true]} // Lock Y translation
                linearDamping={4}
                angularDamping={1}
                position={[
                    PLAYER_CONFIG.startPos.x,
                    PLAYER_CONFIG.startPos.y,
                    PLAYER_CONFIG.startPos.z,
                ]}
                userData={{ name: 'player' }}
            >
                <BallCollider args={[PLAYER_CONFIG.radius]} />
            </RigidBody>
        </group>
    )
}
